/* EC Glow Studio: progressive tabs, optional motion, and a server-rendered bag. */
(() => {
  'use strict';
  const root = () => window.Shopify?.routes?.root || '/';
  const previewURL = path => {
    const url = new URL(path, location.origin);
    const current = new URL(location.href);
    for (const key of ['preview_theme_id', '_fd', 'pb']) if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    return url;
  };
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canAnimate = () => !motion.matches && document.body.dataset.motion === 'true';

  function initializeTabs(scope = document) {
    scope.querySelectorAll('[data-studio-tabs]').forEach(group => {
      if (group.dataset.initialized) return;
      const tabs = [...group.querySelectorAll('[data-studio-tab]')];
      const panels = [...group.querySelectorAll('[data-tab-panel]')];
      if (!tabs.length || tabs.length !== panels.length) return;
      group.dataset.initialized = 'true';
      group.classList.add('studio-tabs-ready');
      group.querySelector('[data-tab-list]').setAttribute('role', 'tablist');
      const select = (index, focus = false) => {
        tabs.forEach((tab, i) => {
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', panels[i].id);
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          panels[i].setAttribute('role', 'tabpanel');
          panels[i].setAttribute('aria-labelledby', tab.id);
          panels[i].tabIndex = 0;
          panels[i].hidden = i !== index;
        });
        if (focus) tabs[index].focus();
      };
      select(0);
      group.addEventListener('click', event => {
        const tab = event.target.closest('[data-studio-tab]');
        if (!tab || !tabs.includes(tab)) return;
        event.preventDefault(); select(tabs.indexOf(tab));
      });
      group.addEventListener('keydown', event => {
        const index = tabs.indexOf(event.target);
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next !== undefined) { event.preventDefault(); select(next, true); }
      });
      group.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-tab-panel]');
        const index = panels.indexOf(panel);
        if (index >= 0) select(index);
      });
    });
  }

  const reveals = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) {
      if (canAnimate()) entry.target.classList.add('studio-reveal-active');
      reveals.unobserve(entry.target);
    }
  }, { threshold: .08 }) : null;
  function initialize(scope = document) {
    initializeTabs(scope);
    scope.querySelectorAll('[data-studio-reveal]').forEach(section => {
      if (canAnimate() && !window.Shopify?.designMode) reveals?.observe(section);
    });
  }
  motion.addEventListener?.('change', () => {
    if (motion.matches) document.querySelectorAll('.studio-reveal-active').forEach(node => node.classList.remove('studio-reveal-active'));
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-studio-reveal]').forEach(section => reveals?.unobserve(section)));

  class ECParallax extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.media = this.querySelector('[data-parallax-media]');
      this.smallScreen = window.matchMedia('(max-width: 749px)');
      this.visible = false;
      this.update = () => {
        this.frame = null;
        const enabled = this.dataset.parallax === 'true' && canAnimate() && !window.Shopify?.designMode && (!this.smallScreen.matches || this.dataset.parallaxMobile === 'true');
        this.classList.toggle('is-parallax', enabled);
        if (!this.media) return;
        if (!enabled || !this.visible) { this.media.style.removeProperty('transform'); return; }
        const box = this.getBoundingClientRect();
        const progress = Math.max(-1, Math.min(1, ((window.innerHeight - box.top) / (window.innerHeight + box.height)) * 2 - 1));
        const strength = Math.max(0, Math.min(60, Number(this.dataset.strength) || 30));
        this.media.style.transform = `translate3d(0,${(progress * strength).toFixed(2)}px,0)`;
      };
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(this.update); };
      this.onScroll = () => { if (this.visible && canAnimate()) this.schedule(); };
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); });
        this.observer.observe(this);
      }
      window.addEventListener('scroll', this.onScroll, {passive:true});
      window.addEventListener('resize', this.schedule, {passive:true});
      motion.addEventListener?.('change', this.schedule);
      this.smallScreen.addEventListener?.('change', this.schedule);
      this.update();
    }
    disconnectedCallback() {
      this.observer?.disconnect(); cancelAnimationFrame(this.frame);
      window.removeEventListener('scroll', this.onScroll); window.removeEventListener('resize', this.schedule);
      motion.removeEventListener?.('change', this.schedule); this.smallScreen?.removeEventListener?.('change', this.schedule);
      this.bound = false;
    }
  }

  class ECQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-quick-content]');
      this.status = this.querySelector('[data-quick-status]');
      this.fallback = this.querySelector('[data-quick-fallback]');
      this.onOpen = event => {
        const trigger = event.target.closest('[data-quick-open]');
        if (!trigger || !this.dialog.showModal || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        const url = previewURL(trigger.href);
        if (url.origin !== location.origin) return;
        event.preventDefault(); this.open(trigger, url);
      };
      this.onClose = () => {
        this.request?.abort(); document.body.classList.remove('studio-quick-open');
        if (this.trigger?.isConnected) this.trigger.focus({preventScroll:true});
        // Keep a pending purchase controller connected until it has completed its request.
      };
      this.onAdded = event => {
        event.detail.sourceTrigger = this.trigger;
        if (document.querySelector('ec-cart')?.dataset.mode !== 'page') this.dialog.close();
      };
      this.onClick = event => { if (event.target.closest('[data-quick-close]')) this.dialog.close(); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('ec:cart-added', this.onAdded);
      this.dialog.addEventListener('close', this.onClose);
      document.addEventListener('click', this.onOpen);
    }
    async open(trigger, url) {
      if (this.content.querySelector('ec-product')?.adding) return;
      this.request?.abort(); this.request = new AbortController();
      const request = this.request;
      this.trigger = trigger; this.fallback.href = url.href;
      this.content.replaceChildren(); this.status.textContent = this.dataset.loading;
      this.status.classList.remove('is-error');
      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('studio-quick-open');
      this.querySelector('[data-quick-close]').focus();
      url.searchParams.set('section_id','studio-quick-product');
      try {
        const response = await fetch(url,{signal:request.signal,credentials:'same-origin',headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error('Quick product unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
        const product = parsed.querySelector('ec-product[data-quick-product]');
        if (!product) throw new Error('Quick product missing');
        if (request !== this.request || !this.dialog.open) return;
        product.querySelectorAll('noscript').forEach(node => node.remove());
        this.content.replaceChildren(document.importNode(product,true)); this.status.textContent = '';
      } catch (error) {
        if (error.name === 'AbortError' || request !== this.request) return;
        this.status.textContent = this.dataset.error; this.status.classList.add('is-error');
      }
    }
    disconnectedCallback() {
      this.request?.abort(); document.body.classList.remove('studio-quick-open');
      document.removeEventListener('click',this.onOpen); this.removeEventListener('click',this.onClick);
      this.removeEventListener('ec:cart-added',this.onAdded); this.dialog?.removeEventListener('close',this.onClose); this.bound=false;
    }
  }

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches('[data-card-form]') || !window.fetch) return;
    event.preventDefault();
    if (form.dataset.busy === 'true' || !form.reportValidity()) return;
    const button = form.querySelector('[data-add-button]'), status = form.querySelector('[data-card-status]');
    const data = new FormData(form);
    form.dataset.busy = 'true'; button.disabled = true; status.textContent = form.dataset.loading;
    try {
      const response = await fetch(root()+'cart/add.js',{method:'POST',credentials:'same-origin',headers:{Accept:'application/json'},body:data});
      const result = await response.json();
      if (!response.ok || result.status) {
        const error = new Error(typeof result.description === 'string' ? result.description : form.dataset.error);
        error.confirmedRejection = true; throw error;
      }
      status.textContent = form.dataset.added;
      form.dispatchEvent(new CustomEvent('ec:cart-added',{bubbles:true,detail:{...result,sourceTrigger:button}}));
      try {
        const response = await fetch(root()+'cart.js',{credentials:'same-origin'});
        if (response.ok) { const cart=await response.json(); document.querySelectorAll('[data-cart-count]').forEach(node=>{node.textContent=cart.item_count;node.hidden=cart.item_count===0;}); }
      } catch (_) { /* A successful add remains successful even if count refresh fails. */ }
    } catch (error) { status.textContent = error.confirmedRejection ? error.message : form.dataset.error; }
    finally { form.dataset.busy='false'; button.disabled=false; form.querySelector('[data-card-cart-link]').hidden=false; }
  });

  class ECCart extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.sequence = 0;
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-cart-content]');
      this.status = this.querySelector('[data-cart-status]');
      this.onOpen = event => {
        const trigger = event.target.closest('[data-cart-open]');
        if (!trigger || this.dataset.mode === 'page' || !this.dialog.showModal || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault(); this.open(trigger);
      };
      this.onAdded = event => {
        if (this.dataset.mode === 'page') return;
        this.open(event.detail?.sourceTrigger || event.target.querySelector?.('[data-add-button]') || document.activeElement, true);
      };
      this.onClick = event => {
        if (event.target.closest('[data-cart-close]')) { this.dialog.close(); return; }
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
          return;
        }
        const line = event.target.closest('[data-cart-line]');
        if (!line || this.busy) return;
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) { event.preventDefault(); this.mutate('cart/change.js', {id: line.dataset.cartLine, quantity: 0}); return; }
        const step = event.target.closest('[data-cart-step]');
        if (!step) return;
        const input = line.querySelector('[data-cart-quantity]');
        const direction = Number(step.dataset.cartStep);
        const min = Number(input.min || 1), increment = Number(input.step || 1);
        const max = input.max ? Number(input.max) : Infinity;
        const quantity = Number(input.value) + direction * increment;
        if (quantity < min || quantity > max) return;
        this.mutate('cart/change.js', {id: line.dataset.cartLine, quantity}, input.id);
      };
      this.onChange = event => {
        if (this.busy) return;
        if (event.target.matches('[data-cart-quantity]')) {
          const input = event.target;
          if (!input.reportValidity()) return;
          this.mutate('cart/change.js', {id: input.closest('[data-cart-line]').dataset.cartLine, quantity: Number(input.value)}, input.id);
        }
        if (event.target.matches('[data-cart-note]')) this.mutate('cart/update.js', {note: event.target.value}, event.target.id);
      };
      this.onSubmit = event => {
        if (this.busy) event.preventDefault();
        // Checkout/update retain Shopify's native POST with all current form fields.
      };
      this.onClose = () => {
        document.body.classList.remove('studio-cart-open');
        if (this.trigger?.isConnected && !this.trigger.disabled) this.trigger.focus({preventScroll: true});
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.dialog.addEventListener('close', this.onClose);
      document.addEventListener('click', this.onOpen);
      document.addEventListener('ec:cart-added', this.onAdded);
    }
    disconnectedCallback() {
      this.readRequest?.abort();
      document.body.classList.remove('studio-cart-open');
      document.removeEventListener('click', this.onOpen);
      document.removeEventListener('ec:cart-added', this.onAdded);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      this.dialog?.removeEventListener('close', this.onClose);
      this.bound = false;
    }
    open(trigger, added = false) {
      if (!this.dialog.showModal) { location.assign(root() + 'cart'); return; }
      if (!this.dialog.open) {
        this.trigger = trigger;
        this.dialog.showModal();
        document.body.classList.add('studio-cart-open');
        this.querySelector('[data-cart-close]')?.focus();
      }
      if (this.busy) { this.refreshAfterMutation = true; return; }
      this.refresh(added);
    }
    setBusy(busy) {
      this.content.inert = busy;
      this.content.setAttribute('aria-busy', String(busy));
    }
    applyHTML(html, focusId) {
      if (typeof html !== 'string') throw new Error('Missing cart section');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-cart-content]');
      if (!content || !content.querySelector('[data-cart-count-value]')) throw new Error('Invalid cart section');
      const count = Number(content.querySelector('[data-cart-count-value]').dataset.cartCountValue);
      if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid cart count');
      this.content.replaceChildren(...[...content.childNodes].map(node => document.importNode(node, true)));
      document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count; node.hidden = count === 0; });
      if (focusId && this.dialog.open) {
        this.restoreFocusId = focusId;
      }
    }
    async readHTML() {
      this.readRequest?.abort();
      this.readRequest = new AbortController();
      const url = previewURL(root() + 'cart');
      url.searchParams.set('sections', this.dataset.sectionId);
      // /cart content-negotiates JSON when Accept is application/json, ignoring sections.
      // Request the storefront representation; the sections response itself is still JSON.
      const response = await fetch(url, {signal: this.readRequest.signal, credentials:'same-origin', cache:'no-store', headers:{Accept:'text/html'}});
      if (!response.ok) throw new Error('Cart unavailable');
      const sections = await response.json();
      const html = sections[this.dataset.sectionId];
      if (typeof html === 'string' && html.includes('data-cart-count-value')) return html;
      // Read-only fallback preserves theme preview and never repeats an add/change.
      const page = await fetch(previewURL(root() + 'cart'), {signal:this.readRequest.signal, credentials:'same-origin', cache:'no-store', headers:{Accept:'text/html'}});
      if (!page.ok) throw new Error('Cart page unavailable');
      return page.text();
    }
    async refresh(added = false) {
      const sequence = ++this.sequence;
      this.setBusy(true);
      this.status.classList.remove('is-error');
      this.status.textContent = this.dataset.loading;
      try {
        const html = await this.readHTML();
        if (sequence !== this.sequence || !this.isConnected) return;
        this.applyHTML(html);
        this.status.textContent = '';
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        this.status.textContent = added ? this.dataset.addedFallback : this.dataset.refreshError;
        this.status.classList.add('is-error');
      } finally {
        if (sequence === this.sequence) this.setBusy(false);
      }
    }
    async mutate(endpoint, data, focusId) {
      if (this.busy) return;
      this.busy = true;
      ++this.sequence;
      this.readRequest?.abort();
      this.setBusy(true);
      this.status.classList.remove('is-error');
      this.status.textContent = this.dataset.loading;
      try {
        const response = await fetch(root() + endpoint, {
          method: 'POST', headers: {'Content-Type':'application/json', Accept:'application/json'},
          credentials:'same-origin',
          body: JSON.stringify({...data, sections:[this.dataset.sectionId], sections_url:(() => {const url=previewURL(root() + 'cart'); return url.pathname + url.search;})()})
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error('Cart rejected or uncertain');
        const html = result.sections?.[this.dataset.sectionId] || await this.readHTML();
        if (!this.isConnected) return;
        this.applyHTML(html, focusId);
        this.status.textContent = this.dataset.updated;
      } catch (_) {
        // Never repeat a cart mutation after a timeout or ambiguous response.
        try { this.applyHTML(await this.readHTML(), focusId); } catch (_) { /* Native cart link remains available. */ }
        this.status.textContent = this.dataset.error;
        this.status.classList.add('is-error');
      } finally {
        this.busy = false;
        this.setBusy(false);
        if (this.restoreFocusId && this.dialog.open) this.querySelector('#' + CSS.escape(this.restoreFocusId))?.focus({preventScroll:true});
        this.restoreFocusId = null;
        if (this.refreshAfterMutation) { this.refreshAfterMutation = false; this.refresh(); }
      }
    }
  }
  customElements.define('ec-cart', ECCart);
  customElements.define('ec-quick-add', ECQuickAdd);
  customElements.define('ec-parallax', ECParallax);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), {once:true});
  else initialize();
})();
